// Curated library of creative best-practices + parser-safe request patterns for ORBIT.
// `insert` strings use {placeholders} the user fills in. Keep phrasings ORBIT-parseable.

export interface CreativeTemplate {
  id: string;
  category: string;
  title: string;
  description: string;
  insert: string;
}

export const TEMPLATE_CATEGORIES = [
  'Request patterns',
  'Hooks & angles',
  'Image prompts',
  'Video prompts',
  'DR frameworks',
] as const;

export const creativeTemplates: CreativeTemplate[] = [
  // A. Request patterns (parser-safe) — phrasings ORBIT parses correctly
  {
    id: 'req-states',
    category: 'Request patterns',
    title: 'N variations across states',
    description: 'Spin one script into per-state variations',
    insert: '4 variations of S{script#} for {State1}, {State2}',
  },
  {
    id: 'req-price',
    category: 'Request patterns',
    title: 'Price / value modify',
    description: 'Swap a price or number in an existing clip',
    insert: 'Modify {clip name}: change ${old} to ${new}',
  },
  {
    id: 'req-hook',
    category: 'Request patterns',
    title: 'Hook test',
    description: 'Generate multiple opening hooks for one clip',
    insert: '4 hook variations of {clip name}',
  },
  {
    id: 'req-avatar',
    category: 'Request patterns',
    title: 'Avatar test',
    description: 'Same script, different presenters',
    insert: 'Make {N} creatives with different avatars for {clip name}',
  },
  {
    id: 'req-ugc-state',
    category: 'Request patterns',
    title: 'UGC by state',
    description: 'UGC-style spins targeted to a state',
    insert: '{N} UGC-style variations of {clip name} for {State}',
  },

  // B. Hooks & angles (direct-response best practice)
  {
    id: 'hook-benefit',
    category: 'Hooks & angles',
    title: 'Benefit-led open',
    description: 'Lead with the payoff in the first 2 seconds',
    insert: "Open with the payoff in the first 2 seconds: '{Audience} in {State} are saving ${amount} on {product}…'",
  },
  {
    id: 'hook-interrupt',
    category: 'Hooks & angles',
    title: 'Pattern interrupt',
    description: 'Break the scroll before the offer',
    insert: 'Start with an unexpected visual/line that breaks the scroll before stating the offer.',
  },
  {
    id: 'hook-pas',
    category: 'Hooks & angles',
    title: 'Problem-Agitate-Solve (PAS)',
    description: 'Pain → vivid → fix → CTA',
    insert: 'Name the pain → make it vivid → present {product} as the fix → CTA.',
  },
  {
    id: 'hook-curiosity',
    category: 'Hooks & angles',
    title: 'Curiosity gap',
    description: 'Open a loop the viewer needs closed',
    insert: "'Here's what {industry} won't tell you about {topic}…'",
  },
  {
    id: 'hook-social',
    category: 'Hooks & angles',
    title: 'Social proof',
    description: 'Lead with the crowd already switching',
    insert: "'Thousands of {audience} already switched to {product} — here's why.'",
  },
  {
    id: 'hook-qualify',
    category: 'Hooks & angles',
    title: 'Self-qualifying question',
    description: 'Call out the exact audience up front',
    insert: "'Own a home in {State}? You may be overpaying for {product}.'",
  },

  // C. Image prompt best practices (Gemini/Imagen)
  {
    id: 'img-structured',
    category: 'Image prompts',
    title: 'Structured prompt',
    description: 'Subject → setting → light → comp → style → lens',
    insert: '{subject}, {setting}, {lighting}, {composition}, {style}, shot on {camera/lens}',
  },
  {
    id: 'img-realism',
    category: 'Image prompts',
    title: 'Anti-AI realism',
    description: 'Kill the glossy AI look',
    insert: "photoreal, natural lighting, real skin texture and imperfections, candid — avoid glossy 'AI' look, no warped hands, no gibberish text",
  },
  {
    id: 'img-framing',
    category: 'Image prompts',
    title: 'Platform framing',
    description: 'Pick the right aspect for the placement',
    insert: 'vertical 9:16 for social feed',
  },

  // D. Video prompt best practices (Seedance/Veo/lipsync DR ads)
  {
    id: 'vid-format',
    category: 'Video prompts',
    title: 'DR format baseline',
    description: 'Vertical, short, one CTA',
    insert: 'Vertical 9:16, 15–30s, ONE clear CTA.',
  },
  {
    id: 'vid-hook',
    category: 'Video prompts',
    title: 'Front-load the hook',
    description: 'First 3 seconds carry the payoff',
    insert: 'First 3 seconds = the hook; front-load the payoff, no slow intro.',
  },
  {
    id: 'vid-captions',
    category: 'Video prompts',
    title: 'Burned captions',
    description: 'Large, readable, on-beat',
    insert: 'Burned captions ON, large + readable, timed on-beat with the voice.',
  },
  {
    id: 'vid-ugc',
    category: 'Video prompts',
    title: 'UGC authenticity',
    description: 'Real over polished for DR',
    insert: 'UGC authenticity beats polish for direct-response — handheld, real room, real person.',
  },
  {
    id: 'vid-pace',
    category: 'Video prompts',
    title: 'Talking-head + b-roll',
    description: 'Cutaways keep the pace up',
    insert: 'Talking-head + b-roll cutaways to keep pace; avoid static full-length.',
  },

  // E. DR frameworks
  {
    id: 'fw-aida',
    category: 'DR frameworks',
    title: 'AIDA',
    description: 'Attention → Interest → Desire → Action',
    insert: 'Attention → Interest → Desire → Action.',
  },
  {
    id: 'fw-bab',
    category: 'DR frameworks',
    title: 'Before-After-Bridge',
    description: 'Before → after → how',
    insert: 'Show the before state → the after with {product} → the bridge (how).',
  },
  {
    id: 'fw-4u',
    category: 'DR frameworks',
    title: "4 U's headline",
    description: 'Useful, Urgent, Unique, Ultra-specific',
    insert: 'Useful, Urgent, Unique, Ultra-specific.',
  },
];
